import { type ComponentProps, useEffect, useMemo, useRef, useState } from 'react';
import { useEntryDialogs } from './useEntryDialogs';
import type { useLoggerContext } from './loggerContext';
import type { FocusPanel } from './FocusPanel';
import { buildTimelineRows } from './timelineRows';
import { groupMembersOf } from './supersetNotebook';
import { resolveRestTransition } from './restTransition';
import { amrapNeedsRepsAt } from './amrapGate';
import { makeToggleLoadedSides } from './bilateralToggle';
import { restCueText } from './restCueText';
import { resolveBoundSetIdx } from './boundSet';
import { useFailConfirm } from './useFailConfirm';
import { useAmrapConfirm } from './useAmrapConfirm';
import { useSetCountHandlers } from './useSetCountHandlers';
import { useFailHandlers } from './useFailHandlers';
import { firstAddedToFocus } from './focusOnAdd';
import { rowIdOf } from './state/loggerDraftTypes';

// Focus state machine + set/exercise handlers for the timeline logger
// (Track R: extracted from TimelineLoggerScreen so the screen stays a thin
// render shell under the 500-line budget). Behaviour-preserving — every value
// returned here was previously computed inline in the screen.
//
// Takes the logger context bag the screen already reads, so it drives the SAME
// flat draft store (next/timeline-logger.md §5). The screen keeps reading the
// render-side ctx fields (styles/theme/t/i18n/...) directly; this hook owns
// only the focus/binding logic and the dispatching handlers.
type LoggerCtx = ReturnType<typeof useLoggerContext>;

export function useTimelineLogger(ctx: LoggerCtx) {
  const {
    draft,
    dispatch,
    t,
    profile,
    getExerciseNameBySlug,
    catalog,
    defaultUnit,
    cycleSetPill,
    mode,
    restTimer,
    restPicker,
    // Owned above the Modal beside the cursor (LG-13) — see useRestAdvance.
    restAdvance,
    // Focus cursor — owned by useLoggerCursor in the LoggerScreen body (above
    // the Modal) so it survives minimize -> resume (LG-13), not local useState
    // here (which the Modal unmount would destroy). Behaviour is otherwise
    // identical: this hook still drives every focus transition through these.
    activeId,
    setActiveId,
    selectedSetIdx,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
  } = ctx;

  // The focus advance is no longer immediate (MX-5 D8): the rest IS the
  // transition, so the decision is taken at log time and APPLIED when the rest
  // ends. restAdvance owns both the pending decision and the cursor move, and
  // it lives above the Modal (LG-13). The old useFocusAdvanceCue — a 1.4s
  // "→ Set 3/4" flash on a card you had already left — is retired: its caption
  // now sits on the rest row for as long as the rest lasts, and survives as a
  // brief pill only on the path where there IS no rest to carry it.

  // Cursor semantics (state now lives in useLoggerCursor, above the Modal):
  //  - activeId = the ROW the FocusPanel enters into, keyed on entryId.
  //    ⛔ It used to be keyed on exerciseId, "unique per live entry" — that
  //    premise was false and it is the whole of LG-39: walk / lift / HIIT walk
  //    / walk is one workout with three rows sharing a slug, so the cursor
  //    resolved to the first one forever and the roster lit all three. A stale
  //    id (after a remove/swap, or carried from a previous session) falls back
  //    to null via resolvedActiveId below, so the panel always targets a live
  //    row. NOT in the reducer: the Phase-1 flatten round-trip nulls
  //    WorkoutState.activeExerciseId every dispatch.
  //  - selectedSetIdx = the set the FocusPanel binds (SetCountRow pill); null =
  //    the default first pending set. Reset on every focus switch and each LOG
  //    SET / ＋ so focus tracks the natural top-down flow.
  //  - rosterExpanded starts EXPANDED so the workout opens on a self-teaching
  //    first screen (empty FocusPanel + every exercise visible); picking an
  //    exercise collapses it, a remove re-expands it (Antenore 2026-06-02).
  //
  // Option B focus, REAFFIRMED (Antenore 2026-06-02): the active card is ALWAYS
  // empty until the athlete's FIRST selection — NO auto-focus at start, for a
  // program, a plan, OR a repeat. Those hydrate a full roster (a repeat even
  // hydrates completed sets), so any "first-undone / last entry" fallback
  // focused a seemingly-RANDOM exercise (his bug report). There is NO derived
  // fallback: resolvedActiveId is the explicitly-picked id or null. Focus is set
  // only by a roster/member tap (focusRow) or a genuine mid-workout add
  // (the effect below); a remove clears it back to null → EmptyFocusCard.
  const resolvedActiveId =
    activeId && draft.entries.some((e) => rowIdOf(e) === activeId)
      ? activeId
      : null;

  // Roster tap / superset tab. Defined here for reading order but it reaches
  // restAdvance below, which is safe: it only ever runs from an event handler,
  // long after this body has finished.
  const focusRow = (id: string) => {
    // An explicit choice of where to be beats a queued one (MX-5 slice 3b): a
    // rest armed on another exercise must not, a minute later, yank the screen
    // off the card the athlete just picked. The countdown keeps running as a
    // clock and stops commanding the focus — until it ENDS, which is when the
    // deferred transition takes them back to the work (TM-18a).
    restAdvance.defer();
    setActiveId(id);
    setSelectedSetIdx(null);
    setRosterExpanded(false);
  };

  const activeEntryIdx = resolvedActiveId
    ? draft.entries.findIndex((e) => rowIdOf(e) === resolvedActiveId)
    : -1;
  const activeEntry = activeEntryIdx >= 0 ? (draft.entries[activeEntryIdx] ?? null) : null;
  // Bound set = which set the FocusPanel binds. FocusPanel writes through on
  // every keystroke, so cycleSetPill sees fresh values at LOG-SET time (PR
  // check + rest timer). The full binding rule (priority + the "every set
  // logged" edge) lives in the pure, unit-tested resolveBoundSetIdx — it has
  // regressed ≥6 times, so it stays outside the hook. KEY: when an exercise is
  // fully logged we bind set 0 (set 1) instead of -1, so reps/weight/RIR stay
  // visible when you tap back into a completed exercise (the "weight disappears
  // on exercise switch" bug, 2026-06-28). Display-only — the superset
  // round-robin advances on LOG, never on focus, so binding here is inert.
  const firstPendingIdx = activeEntry
    ? activeEntry.sets.findIndex((s) => s.completedAt === null)
    : -1;
  const boundSetIdx = resolveBoundSetIdx({
    setCount: activeEntry ? activeEntry.sets.length : 0,
    selectedSetIdx,
    firstPendingIdx,
  });
  const boundSet =
    activeEntry && boundSetIdx >= 0 ? (activeEntry.sets[boundSetIdx] ?? null) : null;

  // Fail-confirm prompt (AP-0p model): failing a rep-based set opens a confirm
  // ("did N, 1 failed → save N-1"). State lives in useFailConfirm so this hook
  // stays under the line cap; onLogFail/onToggleFail call failConfirm.maybePrompt.
  const failConfirm = useFailConfirm({ entries: draft.entries, dispatch });
  // AMRAP-confirm prompt (LG-26): completing a set the program prescribed AMRAP
  // on, without the chip, asks whether it went to the limit.
  const amrapConfirm = useAmrapConfirm({ entries: draft.entries, dispatch });
  // Fail-flash (2026-07-20): a fail now advances like any logged set — it is a
  // normal training outcome, not an error, so it neither stays put nor raises a
  // banner (the old LG-14 danger cue is gone). Instead the just-failed row
  // briefly flashes in the timeline. `prefix` targets that row by its
  // `${entryIdx}-${setIdx}-` key prefix; `key` bumps so a repeat fail on the
  // same set re-fires the flash.
  const [failFlash, setFailFlash] = useState<{
    prefix: string;
    key: number;
  } | null>(null);
  // Catalog exercise for the active entry — the chip row's UNI/BI gate
  // (lateralityModifiable). entry.exerciseId is the slug (card logger keys the
  // same: catalog.find(c => c.slug === entry.exerciseId)).
  const activeExercise = activeEntry
    ? catalog?.find((c) => c.slug === activeEntry.exerciseId)
    : undefined;
  // − removes the trailing set as long as the entry keeps at least one. It stays
  // available even after every planned set is logged: the original "pending-only"
  // guard hid the control once the plan was complete (v2.68.0 smoke — Antenore
  // couldn't trim an over-logged set), but the queue is exactly what he wants to
  // shrink at that point. Removing a logged trailing set is an EXPLICIT tap, not
  // silent destruction (3.b) — full per-row edit/delete still lands in slice 5.
  const lastSetIdx = activeEntry ? activeEntry.sets.length - 1 : -1;
  const canRemove = !!activeEntry && activeEntry.sets.length > 1;

  const effortScale = profile?.effortScale ?? 'rir';
  // Catalog + bodyweight feed the rows' bodyweight pricing (IB-9): a push-up's
  // load is bw×pct, neither of which lives on the draft entry.
  const timelineRows = useMemo(
    () => buildTimelineRows(draft.entries, catalog, profile?.bodyweightKg ?? 0),
    [draft.entries, catalog, profile?.bodyweightKg],
  );

  // Group members of the active exercise's group. minMembers:1 so the group
  // LINE shows from the first member (creating a superset shows the header
  // immediately — Antenore 2026-06-01); the LOG-SET auto-advance below still
  // guards on length >= 2 (a lone member has no "next").
  // Keyed on the group ID, never on the group OBJECT: the id is the identity
  // that matters, and depending on the object would recompute on every render
  // that rebuilds the entry. (It also keeps the deps honest for eslint, which
  // otherwise demands `activeEntry.group` because of the truthiness check.)
  const activeGroupId = activeEntry?.group?.id ?? null;
  const groupMembers = useMemo(
    () =>
      activeGroupId ? groupMembersOf(draft.entries, activeGroupId, 1) : [],
    [draft.entries, activeGroupId],
  );

  // Bug fix (Antenore 2026-06-01): when an exercise is added to the workout the
  // focus must jump to the NEW exercise. The add appends to draft.entries; we
  // detect a freshly-appeared entryId and focus it. The ref starts null so
  // the initial mount doesn't hijack the first-undone resolution above.
  //
  // ⛔ entryId, not exerciseId (LG-39): firstAddedToFocus is a set difference,
  // so adding an exercise the workout ALREADY contains produced an empty
  // "added" list and the focus silently didn't move — the second walk of the
  // day landed nowhere. A fresh row always mints a fresh entryId, so the set
  // difference is correct again for duplicates without touching the helper.
  const prevIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    const ids = draft.entries.map(rowIdOf);
    const prev = prevIdsRef.current;
    prevIdsRef.current = ids;
    if (prev === null) return;
    // Adding/swapping an exercise focuses it (Antenore 2026-06-02: auto-focus
    // on add; the extra tap was a bug), WITHOUT focusing during hydration of a
    // program/plan/repeat, which must stay empty (Option B). Single add + swap +
    // multi-add-to-non-empty focus; []→[many] and roster replaces don't. Pure
    // decision extracted + unit-tested (focusOnAdd.test.ts) — a length-only
    // guard used to drop the swap refocus (Antenore 2026-07-02: multi-add
    // dead-ended on the empty FocusPanel).
    const focusId = firstAddedToFocus(prev, ids);
    if (focusId !== null) {
      setActiveId(focusId);
      setSelectedSetIdx(null);
      setRosterExpanded(false);
    }
    // The three setters come from useLoggerCursor's useState and are
    // referentially stable, so listing them changes nothing at runtime —
    // `draft.entries` stays the only real trigger.
  }, [draft.entries, setActiveId, setSelectedSetIdx, setRosterExpanded]);

  // Confirm dialogs (split group / remove exercise) — extracted to
  // useEntryDialogs (AP-0b 500-line split), behaviour unchanged.
  const { confirmSplitGroup, onRemoveExercise } = useEntryDialogs({
    t,
    dispatch,
    // RW-25 — the host's confirm, straight off the context bag: both dialogs
    // used react-native's Alert, which never fires on react-native-web.
    confirm: ctx.deps.confirm,
    getExerciseNameBySlug,
    activeEntry,
    activeEntryIdx,
    onRemoved: () => {
      // Option B point c: clearing activeId blanks the card (there is
      // no fallback) + re-expand the roster so the athlete re-picks.
      setActiveId(null);
      setSelectedSetIdx(null);
      setRosterExpanded(true);
    },
  });

  const onChangeSet: ComponentProps<typeof FocusPanel>['onChange'] = (
    patch,
  ) => {
    if (activeEntryIdx < 0 || boundSetIdx < 0) return;
    dispatch({
      type: 'update_set',
      entryIdx: activeEntryIdx,
      setIdx: boundSetIdx,
      patch,
    });
  };

  // Log → decide → hand over. Shared by onLogSet and onLogFail: both complete
  // the bound set through cycleSetPill and both advance the same way, which is
  // the 2026-07-20 rule that a fail is a normal outcome and not an error.
  // ⚠️ The transition is resolved against the PRE-dispatch draft, exactly as
  // the advance always was: the just-logged set still reads pending there, and
  // both resolvers exclude it by index.
  // LG-28 gate, shared by EVERY completion leg — onLogFail forgot it once and
  // silently completed the set as an ordinary one. Rationale in amrapGate.ts.
  const blockedOnReps = (i: number) =>
    amrapNeedsRepsAt(draft.entries, activeEntryIdx, i);

  const commitSet = (
    loggedIdx: number,
    opts?: { rir?: number },
  ): { prFlash: boolean } => {
    if (blockedOnReps(loggedIdx)) return { prFlash: false };
    // A LIVE-CUE question: only live logging sits through a rest, so a
    // past-session edit and a prescription both collapse the transition.
    const transition = mode !== 'log'
      ? null
      : resolveRestTransition({
          entries: draft.entries,
          entryIdx: activeEntryIdx,
          loggedSetIdx: loggedIdx,
          groupMembers,
        });
    const committed = cycleSetPill(activeEntryIdx, loggedIdx, {
      ...(opts?.rir !== undefined ? { rir: opts.rir } : {}),
      ...(transition ? { transition } : {}),
    });
    if (transition === null) {
      setSelectedSetIdx(null);
      return committed;
    }
    // A rest is running ⇒ hold the binding on the set just logged, so the card
    // in front of you is still the one you're resting from. Cleared when the
    // transition applies. No rest ⇒ nothing to wait for, move now.
    if (committed.restArmed) setSelectedSetIdx(loggedIdx);
    else setSelectedSetIdx(null);
    restAdvance.arm(transition, committed.restArmed);
    return committed;
  };

  // (MX-5 slice 3) The effect that used to live here — watching hold.target
  // clear to detect a logHold and replay the advance by hand — is gone with
  // logHold itself. A timed set is now completed by the band tap, i.e. by
  // onLogSet, so it takes the ordinary advance below like every other set. The
  // focus re-anchor it did survives too: onLogSet resets selectedSetIdx and the
  // round-robin owns activeId, which is exactly what that effect was patching
  // in ("doesn't advance to the next set", Antenore 2026-06-18).

  // rir (AP-0b): the effort meter completes the set AND records the effort in
  // one tap. The write goes through the same update_set patch the old effort
  // input used ({rir, rpe: ''} — one canonical field), queued BEFORE cycle_set
  // so the reducer lands both in order; the override also rides into
  // cycleSetPill so its MAX-regression check reads the tapped rir, not the
  // stale draft. Guarded on typeof — a bare onPress={onLogSet} would pass the
  // press event here. Returns cycleSetPill's { prFlash } for the gold pulse.
  // `durationSec` (MX-5 slice 3) is the held time when that same tap stopped a
  // running hold/cardio timer: it joins the SAME patch, so one gesture is one
  // write and the completion never lands on a set whose seconds are still
  // missing. No override into cycleSetPill for it, unlike rir: the PR check
  // builds from reps/weight (buildWorkoutSetFromDraft), so a duration set is
  // never PR-eligible and the value would go unread.
  const onLogSet = (rir?: number, durationSec?: number) => {
    if (activeEntryIdx < 0 || boundSetIdx < 0) return undefined;
    const loggedIdx = boundSetIdx;
    // Before the effort dispatch: the band tap writes RIR first.
    if (blockedOnReps(loggedIdx)) return undefined;
    const tappedRir = typeof rir === 'number' ? rir : undefined;
    const heldSec =
      typeof durationSec === 'number' && durationSec > 0 ? durationSec : undefined;
    if (tappedRir !== undefined || heldSec !== undefined) {
      dispatch({
        type: 'update_set',
        entryIdx: activeEntryIdx,
        setIdx: loggedIdx,
        patch: {
          ...(tappedRir !== undefined ? { rir: String(tappedRir), rpe: '' } : {}),
          ...(heldSec !== undefined ? { duration: String(heldSec) } : {}),
        },
      });
    }
    // Completes the bound set: PR check + rest timer + bodyweight nudge, all
    // via the same hook the card logger uses. No add_set here — the next
    // pending set (planned, or one the athlete adds via ＋) becomes the bound
    // target once the rest hands the focus over. The superset round-robin is
    // no longer resolved here: it is one branch of resolveRestTransition, so
    // the hop and the 4s that precedes it come from one answer, not two.
    const committed = commitSet(loggedIdx, {
      ...(tappedRir !== undefined ? { rir: tappedRir } : {}),
    });
    // Ask about a prescribed AMRAP AFTER the commit, so the question is about a
    // set that is already logged: answering it is an edit, and dismissing it
    // leaves an ordinary completed set behind rather than an unfinished one.
    amrapConfirm.maybePrompt(activeEntryIdx, loggedIdx);
    return committed;
  };

  // ✗ handlers (extracted to useFailHandlers — same cap split as the ＋/− pair).
  const { onLogFail, onToggleFail } = useFailHandlers({
    activeEntryIdx,
    boundSetIdx,
    boundSet,
    dispatch,
    commitSet,
    blockedOnReps,
    maybePromptFailConfirm: failConfirm.maybePrompt,
    setFailFlash,
    t,
  });

  // SetCountRow ＋/− handlers (extracted to useSetCountHandlers, LG-14 split).
  const { onAddSet, onRemoveSet } = useSetCountHandlers({
    activeEntryIdx,
    activeEntry,
    defaultUnit,
    lastSetIdx,
    canRemove,
    dispatch,
    setSelectedSetIdx,
  });

  // 🕐 history now opens as a bottom sheet (Antenore 2026-06-01): inline under
  // the FocusPanel it fell below the fold — you had to scroll to see it. The
  // toggle (toggle_entry_history → activeEntry.historyOpen) is unchanged; only
  // the presentation moved to a Modal so it's always visible + dismissible,
  // parity with the ❓ how-to modal.
  const closeHistory = () => {
    if (activeEntryIdx < 0) return;
    dispatch({ type: 'toggle_entry_history', entryIdx: activeEntryIdx, next: false });
  };

  // Bilateral preference toggle (persisted per exercise) — FocusPanel's UNI/BI
  // chip. Lives in bilateralToggle.ts: it is not focus/binding logic.
  const onToggleLoadedSides = makeToggleLoadedSides({
    entry: activeEntry,
    entryIdx: activeEntryIdx,
    dispatch,
  });

  return {
    resolvedActiveId,
    activeEntry,
    activeEntryIdx,
    boundSet,
    boundSetIdx,
    activeExercise,
    canRemove,
    effortScale,
    timelineRows,
    groupMembers,
    selectedSetIdx,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
    focusRow,
    confirmSplitGroup,
    onRemoveExercise,
    onChangeSet,
    onLogSet,
    onLogFail,
    onToggleFail,
    failPrompt: failConfirm.prompt,
    onConfirmFail: failConfirm.confirm,
    onCancelFail: failConfirm.cancel,
    /** The AMRAP-confirm bag (LG-26). `shouldPrompt` is what the effort-commit
     *  leg reads to hold back the once-ever intro instead of stacking modals. */
    amrapConfirm,
    onAddSet,
    onRemoveSet,
    onToggleLoadedSides,
    closeHistory,
    /** Bumped when a transition is APPLIED — drives the FocusPanel's advance
     *  flash, which used to ride on the retired cue hook's key. */
    cueKey: restAdvance.advanceKey,
    /** The rest row's whole prop, or null when the meter keeps the rows. Shown
     *  on the active card whenever a rest is counting. It no longer checks that
     *  the timer's exercise matches the focused one: under LG-29 the whole
     *  logger is LOCKED while resting, so the focus can only be where the
     *  transition put it (the resting set, then the next set/exercise) — there
     *  is no way to be parked on an unrelated card mid-rest, which is what the
     *  old ownership check guarded against. Dropping it also lets the countdown
     *  FOLLOW the focus across a superset hop, where the timer's tag stays the
     *  exercise that armed it. A manual header rest still shows wherever you are. */
    rest: restAdvance.resting
      ? {
          restTimer,
          cueText: restAdvance.pending
            ? restCueText({
                transition: restAdvance.pending,
                entries: draft.entries,
                t,
                resolveName: getExerciseNameBySlug,
              })
            : null,
          onOpenRestPicker: restPicker.open,
          restPickerSaving: restPicker.saving,
        }
      : null,
    /** Whether the running rest also freezes the navigation surfaces (roster,
     *  feed, add-exercise). Separate from `rest` because a rest armed on the
     *  last set shows its countdown like any other but must leave the way out
     *  open — TM-12, and the reasoning lives in useRestAdvance. */
    restFreezesNavigation: restAdvance.freezesNavigation,
    /** Caption for an advance that had NO rest behind it — a zero-rest lift.
     *  The card still flashes, but a flash is colour and colour is never
     *  allowed to be the only cue, so the words come with it for a beat. */
    flashCue: restAdvance.flashCue
      ? restCueText({
          transition: restAdvance.flashCue,
          entries: draft.entries,
          t,
          resolveName: getExerciseNameBySlug,
        })
      : null,
    failFlash,
  };
}
